import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { User } from 'src/app/classes/user';
import { forkJoin, Subject, Subscription } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { DataInput } from '@trivelope/triv-ui-tools-lib';
import { UserService } from 'src/app/services/user.service';
import { MemberService } from 'src/app/services/member.service';
import { Member } from 'src/app/classes/member';
import { Environment } from 'src/app/classes/environment';
import { accordeonMe } from 'src/app/animations';
import { AvatarsService } from 'src/app/services/avatars.service';


@Component({
	selector: 'app-user-management',
	templateUrl: './user-management.component.html',
	styleUrls: [ 'accordeon-ui.scss' ],
	animations: [ accordeonMe ],
})
export class UserManagementComponent implements OnInit, OnDestroy {


	elements: User[] = [];
	currentFilter = "";
	filteredElements: User[] = [];
	members: Member[] = [];
	expandedUserId = "";

	bLoading = true;

	myInputs: { [name: string]: DataInput } = {};

	sortingField = 'mitgliedsname';
	sortingAsc = true;

	private avatarPreloadSubject = new Subject<string[]>();
	private subscriptions = new Subscription();


	constructor(
		private userService: UserService,
		private memberService: MemberService,
		@Inject('environment') private environment: Environment,
		private avatarsService: AvatarsService,
	) {}


	ngOnInit() {

		this.subscriptions.add(
			this.avatarPreloadSubject.pipe(
				debounceTime(100)
			).subscribe(ids => this.preloadAvatars(ids))
		);

		forkJoin( {
			members: this.memberService.getManageableMembers(),
			users: this.userService.getAll()
		} ).subscribe( results => {

			this.bLoading = false;
			this.elements = results.users;
			this.sortElements();

			this.members = results.members;

			for( const member of this.members ) {

				const userId = member.hauptansprechpartnerId;
				if( !userId ) continue;
				const user = this.elements.find( u => u.ebeId == userId );
				if( !user ) continue;
				user.bMainContact = true;

			}

			for( let dataInput of this.environment.user ) {
				if( dataInput.id === "ebePassword" ) continue;
				if( dataInput.id === "ebePasswordCheck" ) continue;
				this.myInputs[ dataInput.id ] = dataInput;
			}

			const arrOptions: { id: string, label: string }[] = [];
			for( const member of this.members ) {
				arrOptions.push( {
					id: member.mitgliedId,
					label: member.mitgliedsname
				})
			}
			if( this.myInputs[ 'mitgliedId' ] ) {
				(this.myInputs[ 'mitgliedId' ] as any).options = arrOptions;
			}
		} );

	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
		this.avatarPreloadSubject.complete();
	}

	sortColumn( field: string ) {

		if( this.sortingField === field ) {
			this.sortingAsc = !this.sortingAsc;
		} else {
			this.sortingField = field;
			this.sortingAsc = true;
		}

		this.sortElements();

	}

	sortElements() {

		this.elements.sort( ( a, b ) => {

			if( this.sortingField == "name" ) {

				const nameA = a.ebeVorname + " " + a.ebeName;
				const nameB = b.ebeVorname + " " + b.ebeName;
				return this.sortingAsc ? nameA.localeCompare( nameB ) : nameB.localeCompare( nameA );

			}

			const valueA = (a[this.sortingField] || '').toString();
			const valueB = (b[this.sortingField] || '').toString();

			return this.sortingAsc ? valueA.localeCompare( valueB ) : valueB.localeCompare( valueA );

		});

		this.applyFilter();

	}


	clickOnUser( userId: string ) {

		const bPrevious = !!this.expandedUserId;

		this.expandedUserId = ( this.expandedUserId == userId ) ? "" : userId;

		if( bPrevious && this.expandedUserId ) {
			setTimeout( () => {
				const elem = window.document.getElementById( "userRow_" + userId );
				if( elem ) {
					elem.scrollIntoView( { behavior: "smooth", block: "start" } );
				}
			}, 500 );
		}

	}

	onFilterChanged( event: Event ) {

		this.currentFilter = (event.target as HTMLInputElement).value.trim().toLocaleLowerCase();
		this.applyFilter();

	}

	private applyFilter() {

		this.filteredElements = this.elements.filter( u => {

			for( const searchField of [ "ebeId", "ebeName", "ebeVorname", "mitgliedId", "mitgliedsname" ] ) {

				if( !u[ searchField ] ) continue;
				const str = ("" + u[ searchField ]).toLowerCase();
				if( str.includes( this.currentFilter ) ) return true;

			}

			return false;

		});

		this.queueAvatarPreload(this.filteredElements);

	}

	refreshList() {

		this.userService.getAll().subscribe( elements => {
			this.elements = elements;
			this.sortElements();
		});

	}

	private queueAvatarPreload(users: User[]): void {
		const ids = users.map(u => u.ebeId).filter(Boolean);
		this.avatarPreloadSubject.next(ids);
	}

	private preloadAvatars(ids: string[]): void {
		const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
		if (!uniqueIds.length) return;
		const chunkSize = 100;
		for (let i = 0; i < uniqueIds.length; i += chunkSize) {
			const chunk = uniqueIds.slice(i, i + chunkSize);
			this.subscriptions.add(
				this.avatarsService.ensureMinis(chunk).pipe(take(1)).subscribe()
			);
		}
	}

	memberHasNewMainPartner( newMainPartner: User ) {

		this.elements
			.filter( u => u.mitgliedId == newMainPartner.mitgliedId )
			.forEach( u => { u.bMainContact = ( u.ebeId == newMainPartner.ebeId ) } );

	}

	/*

		sorting disabled to prevent the UI from jumping when the elements are sorted

	onUserEdited() {

		this.sortElements();

		if( this.expandedUserId ) {
			setTimeout( () => {
				const elem = window.document.getElementById( "userRow_" + this.expandedUserId );
				if( elem ) {
					elem.scrollIntoView( { behavior: "smooth", block: "start" } );
				}
			}, 300 );
		}

	}
	*/

}


